import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { leafHead } from "@/lib/seo/leaf-head";
import { FaqBlock, ResultCard, ReservedResultsArea, SubHubGrid, buildBreadcrumbLd } from "@/components/age-calculator/shared";
import { TripleDateInput, AnalyzeButton, AnalyzingOverlay, useAnalyzeGate, todayISO } from "@/components/age-calculator/premium";
import { AnchorToc, AnchorSection, LifeProgressBar, WeekdayMilestoneGrid, buildAppFaqLd, chronoDiff, CopyResultButton } from "@/components/age-calculator/sub-shell";

const TITLE = "FPSC & PPSC Age Calculator (Pakistan): Government Servant Relaxation";
const DESC = "Verify age eligibility for FPSC CSS, PPSC, SPSC and KPPSC vacancies with a one-tap +10 in-service relaxation toggle plus category-wise upper ceilings.";

const FAQS = [
  { q: "What is the FPSC CSS age limit in Pakistan?", a: "For most CSS and FPSC posts the standard band is 21-30 years. With the 10-year general relaxation the effective upper limit becomes 40 years for eligible candidates." },
  { q: "Do government servants get age relaxation for PPSC?", a: "Yes. In-service employees with prescribed continuous service typically get +10 years relaxation over the advertised upper limit, backed by a service certificate." },
  { q: "Is age counted on the closing date of application?", a: "Yes. Age is frozen on the closing date printed in the advertisement — not the test day. One day over the ceiling disqualifies you." },
  { q: "Which posts do NOT allow age relaxation?", a: "Posts marked 'No age relaxation admissible' in the advertisement — typically judicial and defence-linked cadres." },
];

const TOC = [
  { id: "calculator", label: "Calculator" },
  { id: "posts", label: "Post-wise Limits" },
  { id: "relaxation", label: "In-Service Relaxation" },
  { id: "documents", label: "Docs Needed" },
  { id: "faqs", label: "FAQs" },
];

export const Route = createFileRoute("/age-calculator/fpsc-ppsc-jobs")({
  head: () => leafHead({
    path: "/age-calculator/fpsc-ppsc-jobs",
    title: TITLE,
    description: DESC,
    scripts: [
      buildAppFaqLd({ name: "FPSC PPSC Age Calculator", description: DESC, faqs: FAQS }),
      buildBreadcrumbLd([
        { name: "Home", url: "https://drhint.com/" },
        { name: "Age Calculator", url: "https://drhint.com/age-calculator" },
        { name: "FPSC & PPSC Jobs", url: "https://drhint.com/age-calculator/fpsc-ppsc-jobs" },
      ]),
    ],
  }),
  component: Page,
});

function Page() {
  const [dob, setDob] = useState("");
  const [closing, setClosing] = useState(todayISO());
  const [minAge, setMinAge] = useState(21);
  const [maxAge, setMaxAge] = useState(30);
  const [govServant, setGovServant] = useState(false);
  const { analyzing, revealed, trigger, reset } = useAnalyzeGate();

  const effectiveMax = govServant ? maxAge + 10 : maxAge;
  const r = useMemo(() => (dob ? chronoDiff(dob, closing) : null), [dob, closing]);
  const eligible = r ? r.years >= minAge && r.years <= effectiveMax : false;

  const copyText = r ? `PSC age on ${closing}: ${r.years}y ${r.months}m ${r.days}d — Band ${minAge}-${effectiveMax}${govServant ? " (+10 relax)" : ""} — ${eligible ? "ELIGIBLE" : "OVER-AGE"}` : "";

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 pt-10 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/age-calculator" className="hover:text-primary">Age Calculator</Link> / FPSC & PPSC Jobs
        </nav>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{TITLE}</h1>
        <p className="mt-3 text-muted-foreground max-w-3xl">
          Pakistan ke public-service commissions — <strong>FPSC CSS, PPSC, SPSC, KPPSC, BPSC</strong> — ke liye dedicated checker with a one-tap <em>Government Servant +10 year</em> relaxation toggle. Basic maths ke liye <Link to="/age-calculator" className="text-primary underline">general age calculator</Link> istemal karein.
        </p>
      </section>

      <AnchorToc items={TOC} />

      <section id="calculator" className="mx-auto max-w-5xl px-4 mt-4 scroll-mt-24">
        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <TripleDateInput label="Date of Birth" value={dob} onChange={(v) => { setDob(v); reset(); }} max={todayISO()} />
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => { setMinAge(21); setMaxAge(30); reset(); }} className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20">CSS 21-30</button>
                <button type="button" onClick={() => { setMinAge(18); setMaxAge(25); reset(); }} className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20">Clerk 18-25</button>
                <button type="button" onClick={() => { setMinAge(20); setMaxAge(35); reset(); }} className="px-3 py-1.5 rounded-full text-xs font-bold bg-primary/10 text-primary hover:bg-primary/20">Lecturer 20-35</button>
              </div>
            </div>
            <div>
              <TripleDateInput label="Closing Date of Application" value={closing} onChange={(v) => { setClosing(v); reset(); }} quickChips />
              <div className="grid grid-cols-2 gap-2 mt-3">
                <label className="text-xs font-semibold">Min age
                  <input type="number" min={16} max={40} value={minAge} onChange={(e) => { setMinAge(Math.max(16, Math.min(40, +e.target.value || 16))); reset(); }} className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                </label>
                <label className="text-xs font-semibold">Max age
                  <input type="number" min={20} max={65} value={maxAge} onChange={(e) => { setMaxAge(Math.max(20, Math.min(65, +e.target.value || 30))); reset(); }} className="mt-1 w-full rounded-lg border border-border bg-background px-2 py-1.5 text-sm" />
                </label>
              </div>
              <label className="mt-3 inline-flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={govServant} onChange={(e) => { setGovServant(e.target.checked); reset(); }} className="accent-primary" />
                <span className="font-medium">Government Servant (+10 years relaxation)</span>
              </label>
            </div>
          </div>
          <AnalyzeButton onClick={trigger} disabled={!dob || analyzing} label="Check PSC Eligibility" />
          <AnalyzingOverlay visible={analyzing} />
          <ReservedResultsArea minHeight={260}>
            {revealed && r ? (
              <>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  <ResultCard label="Age on Closing" value={`${r.years}y ${r.months}m ${r.days}d`} />
                  <ResultCard label="Required Band" value={`${minAge} – ${effectiveMax}`} sub={govServant ? "+10 in-service" : "Standard"} />
                  <ResultCard label="Years to Ceiling" value={Math.max(0, effectiveMax - r.years) + " yr"} />
                  <ResultCard label="Status" value={eligible ? "ELIGIBLE ✅" : "OVER-AGE ❌"} sub={eligible ? "Apply before closing" : "Look for higher-band posts"} />
                </div>
                <div className="mt-4 flex justify-center"><CopyResultButton text={copyText} label="Copy for Application" /></div>
                <LifeProgressBar dob={dob} expectancy={71} />
                <WeekdayMilestoneGrid dob={dob} />
              </>
            ) : !analyzing ? (
              <div className="mt-6 rounded-2xl border border-dashed border-border/70 p-8 text-center text-sm text-muted-foreground">Enter DOB and closing date, then tap Check.</div>
            ) : null}
          </ReservedResultsArea>
        </div>
      </section>

      <AnchorSection id="posts" title="Popular FPSC / PPSC Posts & Age Bands">
        <ul>
          <li><strong>CSS (BPS-17):</strong> 21–30 years.</li>
          <li><strong>Assistant Director / Section Officer:</strong> 22–30.</li>
          <li><strong>Lecturer / Subject Specialist:</strong> 21–35 (province-wise).</li>
          <li><strong>Junior Clerk / Naib Qasid:</strong> 18–25.</li>
          <li><strong>Judicial Magistrate:</strong> 22–35 — usually no relaxation.</li>
        </ul>
      </AnchorSection>

      <AnchorSection id="relaxation" title="How the +10 In-Service Relaxation Works">
        <p>Per Recruitment Rules, an in-service candidate with the prescribed continuous service gets a <strong>10-year one-time general relaxation</strong> over the advertised upper limit. This is <em>not automatic</em> — you must attach a service certificate from the appointing authority. PPSC, SPSC, KPPSC and BPSC mirror this policy with their own certificate formats.</p>
      </AnchorSection>

      <AnchorSection id="documents" title="Documents to Attach for Age Verification">
        <ul>
          <li>Matric certificate (primary DOB proof — preferred over CNIC).</li>
          <li>CNIC front & back.</li>
          <li>Service certificate — only if claiming relaxation.</li>
          <li>Domicile certificate for province-quota posts.</li>
          <li>Departmental NOC for federal / provincial employees.</li>
        </ul>
      </AnchorSection>

      <FaqBlock faqs={FAQS} />
      <SubHubGrid />
    </Layout>
  );
}